import marked from 'marked';

const TEMP_URL_STR = 'TEMP_URL_STR_FOR_LINKS';

const rxLinebreak = /(?:\r\n|\r|\n)/g;

const rxSizeTagsLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;

const rxSymbols = /[&<>"'/]/g;

const rxTableOpeningTag = new RegExp('\\[table]', 'g');

// eslint-disable-next-line
const rxDomainURL = /http[s]*:\/\/[^\/]*\.?rackspace\.(com|net)(\/[a-zA-Z0-9-_~!$&'()*+,;=:@%.]+)*(\/?\?[A-Za-z0-9:/?#%@!$&'()*+,;=\-\.\_~]*)*/gi;

const entityMap = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;',
};

const closeTagMap = {
  b: '** ',
  i: '* ',
  s: '~~ ',
  p: '',
  code: '\n``` ',
};

const openTagMap = {
  b: '**',
  i: '*',
  s: '~~',
  p: '\n',
  code: '```\n',
};

/* Unsupported BB Code Tags: u,  left, center, right, color, bg, style */
const droppedTags = [
  'u',
  'left',
  'center',
  'right',
  'color',
  'bg',
  'style',
  'youtube',
];

const rxDroppedTags = new RegExp(
  '\\[/?(' + droppedTags.join('|') + ')([=\\s].+?)?\\]',
  'gim',
);

const rxOpeningTags = new RegExp(
  '\\[(' + Object.keys(openTagMap).join('|') + ')([=\\s].+?)?\\]',
  'gim',
);

const rxClosingTags = new RegExp(
  '\\[/(' + Object.keys(closeTagMap).join('|') + ')\\]',
  'gim',
);

const drop = () => '';

const linkify = (url: string) => {
  return (
    '<a target="_blank" rel="noopener noreferrer" href="' +
    url +
    '"' +
    '>' +
    url +
    '</a>'
  );
};

const toMdImage = (imgText: string) => {
  /*
   * Parses a bbcode 'img' tag into two capture groups:
   * 1. Alt Text
   * 2. Contents
   * Example: [img alt={alt}]{contents}[/img]
   */

  const rxImg = new RegExp(
    '\\[img[=\\s]?(?:alt="(.*?)?")?(?:.*?)\\](.*?)\\[/img\\]',
    'i',
  );

  return imgText.replace(
    rxImg,
    (match, alt, contents) => `![${alt || ''}](${contents || ''})`,
  );
};

const toMdLink = (linkText: string) => {
  /*
   * Parses a bbcode 'url' or 'link' tag into two capture groups:
   * 1. Link
   * 2. Contents
   * Example: [url={link}]{contents}[/url]
   */

  const rxLink = new RegExp(
    '\\[(?:url|link)(?:="?)?(.*?)"?\\](.*?)\\[/(?:url|link)\\]',
    'i',
  );

  return linkText.replace(
    rxLink,
    (match, link, contents) => `[${contents}](${link ? link : contents})`,
  );
};

const toHtmlSize = (text: string) => {
  // Regular expression to match the [size] tags
  const sizeRegex = new RegExp('\\[size=(\\d+)]([^]*?)\\[/size]', 'gi');
  // Replace [size] tags with appropriate HTML
  return text.replace(sizeRegex, (match, size, content) => {
    return `<span style="font-size: ${size}px;">${content}</span>`;
  });
};

const toMdListItems = (
  listItemText: string,
  ordered: boolean = false,
  asHtml: boolean = false,
) => {
  /*
   * Parses a bbcode list items into one capture group:
   * 1. Contents
   * Example: [li]{contents}[/li] or [*]{contents} or *{contents}
   */

  const rxListItems = new RegExp(
    '(?:\\[li\\]|\\[\\*\\]|\\*)([^\\[\\*]*)(?:\\[/li\\]|\\n)?',
    'gim',
  );

  return listItemText.replace(rxListItems, (match, contents) => {
    return asHtml
      ? `<li>${contents.trim()}</li>\n`
      : ordered
        ? `1. ${contents.trim()}\n`
        : `* ${contents.trim()}\n`;
  });
};

const toMdList = (listText: string) => {
  /*
   * Parses a bbcode list into one capture group:
   * 1. List Option (1, a, or A)
   * 1. Contents (including newlines)
   * Example: [list]{contents}[/list]
   */

  const rxList = new RegExp(
    '\\[list(?:[=\\s]"?)?(.*?)"?\\]([^]*?)\\[/list\\]',
    'gim',
  );

  return listText.replace(rxList, (match, option, contents) => {
    const items = contents.replace(/[\n\r]+/gim, '');

    switch (option) {
      case '1':
        return `${toMdListItems(items, true)}\n`;
      case 'a':
        return `<ol type="a">\n${toMdListItems(items, false, true)}</ol>\n`;
      case 'A':
        return `<ol type="A">\n${toMdListItems(items, false, true)}</ol>\n`;
      default:
        return `${toMdListItems(items)}\n`;
    }
  });
};

const toMdQuote = (quoteText: string) => {
  /*
   * Parses a bbcode quote into one capture group:
   * 1. Author
   * 1. Contents
   * Example: [quote]{contents}[/quote]
   */
  const rxQuote = new RegExp(
    '\\[quote(?:="?)?(.*?)"?\\]([^]*?)\\[/quote\\]',
    'gim',
  );

  return quoteText.replace(
    rxQuote,
    (match, author, contents) =>
      `\n>${author ? ` **${author}**` : ''} ${contents}\n\n`,
  );
};

const toMdSearch = (template: string, label: string) => (
  searchText: string,
) => {
  /*
   * Parses a bbcode search tag into one capture group:
   * 1. Search Term
   * 2. Contents
   * Example: [wiki]{contents}[/wiki]
   */
  const rxSearch = new RegExp(
    '\\[(?:wiki|google|dictionary|dict)(?:="?)?(.*?)"?\\](.*?)\\[/(?:wiki|google|dictionary|dict)\\]',
    'i',
  );

  return searchText.replace(rxSearch, (match, term, contents) => {
    const searchLabel = label.replace('%s', contents || '');
    const searchUrl = template.replace(
      '%s',
      encodeURIComponent(term || contents || ''),
    );
    return `[${searchLabel}](${searchUrl})`;
  });
};

const toMdTableCells = (cellText: string) => {
  /*
   * Parses a bbcode 'th' or 'td' tag into one capture group:
   * 1.  Contents
   * Example: [th]{contents}[/th] or [td]{contents}[/td]
   */

  const rxTableCells = new RegExp(
    '\\[(?:th|td)(?:[=\\s].+?)?\\]([^]*?)\\[/(?:th|td)\\]',
    'gim',
  );

  return cellText.replace(
    rxTableCells,
    (match, contents) => `|${contents.trim()}`,
  );
};

const toMdTableRows = (rowText: string) => {
  /*
   * Parses bbcode 'tr' tags into one capture group:
   * 1.  Contents
   * Example: [tr]{contents}[/tr]
   */
  const rxTableRows = new RegExp(
    '\\[(?:tr)(?:[=\\s].+?)?\\]([^]*?)\\[/(?:tr)\\]',
    'gim',
  );

  let index = 0;

  return rowText.replace(rxTableRows, (match, contents) => {
    let cells = toMdTableCells(contents.trim()).replace(/[\n\r]+/gim, '');

    // If the row has 'th' tags, create a row of dividers equal to the number of 'th' tags.
    const headers =
      contents.match(/\[th\]/gim) ||
      (index === 0 && contents.match(/\[td](\[b\])?/gim)) ||
      [];
    if (headers.length > 0) {
      const dividers = [...Array(headers.length)].map((_) => '|-').join('');
      cells += `\n${dividers}`;
    }
    index++;
    return cells;
  });
};

const toMdTable = (tableText: string) => {
  /*
   * Parses a bbcode 'table' tag into one capture group:
   * 1.  Contents
   * Example: [table]{contents}[/table]
   */

  const rxTable = new RegExp(
    '\\[(?:table)(?:[=\\s].+?)?\\]([^]*?)\\[/(?:table)\\]',
    'gim',
  );

  const tableTextWithLineBreaks = tableText.replace(
    new RegExp('/tr]|table]', 'g'),
    (match, index) => {
      /*
       * Adds line-break after [table] and [/tr] if no-line break found after [table] and [/tr].
       * Example :  [table][tr]{contents}[/tr][/table]
       */
      if (!tableText.charAt(index + match.length).match(rxLinebreak)) {
        return match + '\n';
      } else {
        return match;
      }
    },
  );

  return tableTextWithLineBreaks.replace(rxTable, (match, contents) =>
    toMdTableRows(contents),
  );
};

const rxTableOpeningTagFn = (match: string, index: number, bbcode: string) => {
  /*
   * Adds line-break before [table] if string has no line-break before [table]
   * Example : This is text before table. [table]{contents}[/table]
   */
  if (!bbcode.charAt(index - 1).match(rxLinebreak)) {
    return '\n' + match;
  } else {
    return match;
  }
};

// handles the special tags
const specialTagMap = {
  img: toMdImage,
  list: toMdList,
  ul: toMdList,
  ol: toMdList,
  quote: toMdQuote,

  wiki: toMdSearch(
    'http://en.wikipedia.org/wiki/Special:Search?search=%s',
    "Search Wikipedia for '%s'",
  ),
  google: toMdSearch(
    'http://www.google.com/search?hl=en&q=%s',
    "Search Google for '%s'",
  ),
  dictionary: toMdSearch(
    'http://dictionary.reference.com/browse/%s',
    "Search Dictionary for '%s'",
  ),
  dict: toMdSearch(
    'http://dictionary.reference.com/browse/%s',
    "Search Dictionary for '%s'",
  ),

  table: toMdTable,
};

const specialTags = Object.keys(specialTagMap);

const rxSpecialTags = new RegExp(
  '\\[(' +
    specialTags.join('|') +
    ')([=\\s].+?)?\\]([^]*?)\\[\\/(' +
    specialTags.join('|') +
    ')\\]',
  'gim',
);

const rxSpecialTagsSize = new RegExp(
  '\\[(size)([=\\s].+?)?\\]([^]*?)\\[\\/(size)\\]',
  'gim',
);

const rxSpecialTagsLink = new RegExp(
  '\\[(url|link)([=\\s].+?)?\\]([^]*?)\\[\\/(url|link)\\]',
  'gim',
);

const toSizeLinkTag = (text: string, url: string) => {
  return `<a href="${url}" target="_blank">${text}</a>`;
};

const replaceUrlsWithTemp = (text: string) => {
  const tempURLMap = {} as Record<string, string>;
  let counter = 0;
  const textWithTempURLs = text.replace(rxDomainURL, (match) => {
    counter++;
    const tempURL = `${TEMP_URL_STR}_${counter}`;
    tempURLMap[tempURL] = match;
    return tempURL;
  });
  const tempURLPlaceholderRegex =
    counter > 0 ? new RegExp(Object.keys(tempURLMap).join('|'), 'g') : '';
  return { textWithTempURLs, tempURLMap, tempURLPlaceholderRegex };
};

const renderer = new marked.Renderer();
// Adds target="_blank" for the links
renderer.link = function(href, title, text) {
  return (
    '<a target="_blank" rel="noopener noreferrer" href="' +
    href +
    '"' +
    (title !== null ? ' "title="' + title : '') +
    '>' +
    text +
    '</a>'
  );
};

const MarkupUtils = {
  commentWithNoBBcodeTags: (text: string) => {
    // Regular expression to match BBcode tags
    const bbcodePattern = /\[([a-z]+)(?:=[^\]]+)?\](.*?)\[\/\1\]/i;

    // Check if the text contains BBcode tags
    return !bbcodePattern.test(text);
  },
  /** Translates BB Code to Markdown then returns Markdown-compiled HTML */
  bbCodeToMdHtml: (bbcode: string) =>
    marked(
      bbcode
        .replace(rxDroppedTags, drop)
        .replace(rxTableOpeningTag, (match, index) =>
          rxTableOpeningTagFn(match, index, bbcode),
        )
        .replace(rxSpecialTagsSize, (match, tag) => toHtmlSize(match))
        .replace(rxSpecialTagsLink, (match, tag) => toMdLink(match))
        .replace(rxSizeTagsLinkRegex, (_, text, url) =>
          toSizeLinkTag(text, url),
        )
        .replace(rxSpecialTags, (match, tag) => specialTagMap[tag](match))
        .replace(rxOpeningTags, (_, tag) => openTagMap[tag])
        .replace(rxClosingTags, (_, tag) => closeTagMap[tag]),
      { renderer: renderer },
    ),

  /** Translates special symbols to web-safe equivalent */
  toWebSafe: (text: string) => {
    /**
     * SDP-4222: Replace URLs in text with temporary strings to be replaced with actual links later.
     * Need to do this because we are encoding the comment text first which will encode all characters in the urls within the text.
     * Because of this url regex will not be able to match any url in the encoded text.
     */
    const {
      textWithTempURLs,
      tempURLMap,
      tempURLPlaceholderRegex,
    } = replaceUrlsWithTemp(text);
    return (
      textWithTempURLs
        .replace(rxSymbols, (s) => entityMap[s])
        .replace(rxLinebreak, '<br />')
        // replace the placeholder links tags with actual markup
        .replace(
          tempURLPlaceholderRegex,
          (match) => (match ? linkify(tempURLMap[match]) : match),
        )
    );
  },
};

export { MarkupUtils };
